import type { Command } from "../document/commands.ts";
import type { MapDocument, TileRef, TilesetRef } from "../document/types.ts";
import { selectedProps } from "./many-props.ts";
import type { ObjectTypeDefinition } from "./objects.ts";
import { capturePreset, presetApplication, presetIssues, type BehaviourPreset, type PresetFrameSize } from "./presets.ts";
import type { PropRef } from "./templates.ts";

/**
 * The store's side of behaviour presets (P1.6-T34, ADR-070's amendment "behaviour presets"): saving the lone
 * selected prop's behaviour as a preset, and applying a preset to the selected props whose frame fits as one
 * command that one undo takes back. A flagged preset (a type the game does not register, a frame the world
 * does not hold) applies nowhere, whoever asks. The presets themselves are the host's; nothing here keeps one.
 */

/**
 * What the edits need from the store.
 */
export interface PresetEditsHost {
  readonly map: () => MapDocument;
  readonly run: (command: Command) => boolean;
  readonly selection: () => readonly PropRef[];
  readonly types: () => readonly ObjectTypeDefinition[];
  readonly tilesets: () => readonly TilesetRef[];
  readonly sizeOf: PresetFrameSize;
}

/**
 * What an Apply did: how many selected props fit and how many were left out, and whether any behaviour
 * changed (false when every fitting prop already carried it).
 */
export interface PresetApplied {
  readonly fitting: number;
  readonly leftOut: number;
  readonly changed: boolean;
}

/**
 * Presets over the prop selection.
 */
export class PresetEdits {
  /**
   * @param host The store's side.
   */
  public constructor(private readonly host: PresetEditsHost) {}

  /**
   * Why the selection cannot be saved as a preset, or null when it can: one selected prop carrying a
   * behaviour of a type the game registers, Copy behaviour's rule.
   *
   * @returns The reason, or null.
   */
  public saveRefusal(): string | null {
    const props = selectedProps(this.host.map(), this.host.selection());
    const only = props[0]?.prop;
    if (props.length !== 1 || only === undefined) {
      return "Select one prop carrying a behaviour to save it as a preset";
    }
    if (only.behaviour === undefined) {
      return "The selected prop carries no behaviour";
    }
    const type = only.behaviour.type;
    return this.host.types().some((t) => t.type === type) ? null : `This game does not register ${type}, so it is not saved`;
  }

  /**
   * The preset the lone selected prop gives.
   *
   * @param name The preset's name.
   * @returns The preset.
   * @throws Error with `saveRefusal`'s reason, or when the name is bad.
   */
  public capture(name: string): BehaviourPreset {
    const refusal = this.saveRefusal();
    const only = selectedProps(this.host.map(), this.host.selection())[0]?.prop;
    if (refusal !== null || only === undefined) {
      throw new Error(refusal ?? "Select one prop carrying a behaviour to save it as a preset");
    }
    return capturePreset(only, name);
  }

  /**
   * Why a preset cannot be applied in this world, empty when it can.
   *
   * @param preset The preset.
   * @param frameExists Whether the editor's atlas holds a frame.
   * @returns The problems.
   */
  public issues(preset: BehaviourPreset, frameExists: (frame: TileRef) => boolean): string[] {
    return presetIssues(
      preset,
      this.host.tilesets(),
      frameExists,
      this.host.types().map((t) => t.type),
    );
  }

  /**
   * The preset frame's size, or undefined when it cannot be sized.
   *
   * @param preset The preset.
   * @returns The size.
   */
  public frameSize(preset: BehaviourPreset): { readonly w: number; readonly h: number } | undefined {
    return this.host.sizeOf(preset.frame);
  }

  /**
   * How many selected props fit a preset, of how many selected.
   *
   * @param preset The preset.
   * @returns The counts.
   */
  public fit(preset: BehaviourPreset): { readonly fitting: number; readonly selected: number } {
    const application = presetApplication(this.host.map(), this.host.selection(), preset, this.host.sizeOf);
    return { fitting: application.fitting.length, selected: application.fitting.length + application.leftOut.length };
  }

  /**
   * Why a preset cannot be applied to the selection, or null when it can: a flag, no selection, or no selected
   * prop whose frame fits.
   *
   * @param preset The preset.
   * @param frameExists Whether the editor's atlas holds a frame.
   * @returns The reason, or null.
   */
  public applyRefusal(preset: BehaviourPreset, frameExists: (frame: TileRef) => boolean): string | null {
    const issue = this.issues(preset, frameExists)[0];
    if (issue !== undefined) {
      return `Cannot apply: ${issue}`;
    }
    const { fitting, selected } = this.fit(preset);
    if (selected === 0) {
      return "Select the props to apply it to";
    }
    if (fitting === 0) {
      const size = this.frameSize(preset);
      return size === undefined ? "Its frame cannot be sized, so no frame fits it" : `No selected prop's frame is ${size.w} by ${size.h}`;
    }
    return null;
  }

  /**
   * Gives every selected prop whose frame fits a behaviour equal to the preset's, as one command.
   *
   * @param preset The preset.
   * @param frameExists Whether the editor's atlas holds a frame.
   * @returns What it did, or null when `applyRefusal` refuses or a lock or a hidden layer refused the command.
   */
  public apply(preset: BehaviourPreset, frameExists: (frame: TileRef) => boolean): PresetApplied | null {
    if (this.applyRefusal(preset, frameExists) !== null) {
      return null;
    }
    const { command, fitting, leftOut } = presetApplication(this.host.map(), this.host.selection(), preset, this.host.sizeOf);
    if (command !== null && !this.host.run(command)) {
      return null;
    }
    return { fitting: fitting.length, leftOut: leftOut.length, changed: command !== null };
  }
}
